import { useState } from 'react';
import { useParams } from 'react-router-dom';
import { useApp, useAsync } from '../lib/app-context';
import { MediaSlots } from '../components/MediaSlots';
import { Async, Badge, Card, Empty, Field, NumberField, TagsField, TextField } from '../components/ui';
import { DeleteButton } from './Exercises';
import {
    DIMENSION_LABELS,
    type Bound, type Exercise, type ExerciseBlock, type ExercisePrescription,
    type MeasurementDimension, type Session, type SetGrouping, type SetPrescription,
} from '../lib/types';

const uid = () => crypto.randomUUID().slice(0, 8);

export function Sessions() {
    const { moduleId } = useParams();
    const { data } = useApp();
    const state = useAsync(async () => ({
        sessions: await data.listSessions(moduleId!),
        exercises: await data.listExercises(moduleId!),
    }), [moduleId]);
    const [editingId, setEditingId] = useState<string | 'new' | null>(null);

    return (
        <Async state={state}>
            {({ sessions, exercises }) => {
                if (editingId) {
                    const session = editingId === 'new' ? null : sessions.find((s) => s.id === editingId) ?? null;
                    return (
                        <SessionEditor
                            key={editingId}
                            moduleId={moduleId!}
                            session={session}
                            exercises={exercises}
                            onDone={() => { setEditingId(null); state.reload(); }}
                            onCancel={() => setEditingId(null)}
                        />
                    );
                }

                return (
                    <>
                        <div className="page-head">
                            <div>
                                <h1>Sessions</h1>
                                <p className="muted">
                                    A session is one workout. Sessions attached to the programme tree
                                    appear as days; the rest form the standalone Workouts library.
                                </p>
                            </div>
                            <button className="btn" disabled={exercises.length === 0}
                                onClick={() => setEditingId('new')}>New session</button>
                        </div>

                        {exercises.length === 0 && (
                            <div className="banner" style={{ marginBottom: 16 }}>
                                Add at least one exercise first — a session has nothing to prescribe without one.
                            </div>
                        )}

                        {sessions.length === 0 ? (
                            <Empty title="No sessions yet" />
                        ) : (
                            <div className="list">
                                {sessions.map((session) => {
                                    const prescriptions = session.blocks.flatMap((b) => b.exercises);
                                    const recorded = prescriptions.filter((p) => 'recorded' in p.completion).length;
                                    return (
                                        <div key={session.id} className="list-row">
                                            <div style={{ flex: 1, minWidth: 0 }}>
                                                <div className="title">{session.title}</div>
                                                <div className="small muted">{session.summary || session.slug}</div>
                                            </div>
                                            {session.is_free_sample && <Badge tone="teal">Free</Badge>}
                                            <Badge>{prescriptions.length} exercises</Badge>
                                            <Badge tone={recorded > 0 ? 'coral' : 'neutral'}>
                                                {recorded} recorded
                                            </Badge>
                                            <button className="btn secondary small"
                                                onClick={() => setEditingId(session.id)}>Edit</button>
                                            <DeleteButton label={session.title}
                                                onDelete={() => data.deleteSession(session.id)}
                                                onDone={state.reload} />
                                        </div>
                                    );
                                })}
                            </div>
                        )}
                    </>
                );
            }}
        </Async>
    );
}

// --- Editor ----------------------------------------------------------------------

function emptyPrescription(exerciseId: string): ExercisePrescription {
    return {
        id: uid(),
        exerciseID: exerciseId,
        notes: '',
        completion: { automatic: {} },
        sets: [{ targets: [{ dimension: 'repetitions', bound: { exactly: { _0: 10 } } }] }],
    };
}

function SessionEditor({ moduleId, session, exercises, onDone, onCancel }: {
    moduleId: string; session: Session | null; exercises: Exercise[];
    onDone: () => void; onCancel: () => void;
}) {
    const { data } = useApp();
    const [form, setForm] = useState<Omit<Session, 'id' | 'module_id'>>({
        slug: session?.slug ?? '',
        title: session?.title ?? '',
        summary: session?.summary ?? '',
        tags: session?.tags ?? [],
        blocks: session?.blocks ?? [],
        estimated_seconds: session?.estimated_seconds ?? 1800,
        is_free_sample: session?.is_free_sample ?? false,
        attribute_weights: session?.attribute_weights ?? {},
        cover_media_id: session?.cover_media_id ?? null,
        video_media_id: session?.video_media_id ?? null,
    });
    const [error, setError] = useState<string | null>(null);

    const update = (patch: Partial<typeof form>) => setForm({ ...form, ...patch });
    const updateBlock = (index: number, block: ExerciseBlock) =>
        update({ blocks: form.blocks.map((b, i) => (i === index ? block : b)) });

    async function save() {
        setError(null);
        try {
            await data.upsertSession({
                ...(session ? { id: session.id } : {}),
                module_id: moduleId,
                ...form,
                slug: form.slug || form.title.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, ''),
            });
            onDone();
        } catch (e) {
            setError(e instanceof Error ? e.message : String(e));
        }
    }

    return (
        <>
            <div className="page-head">
                <div>
                    <div className="eyebrow">Session</div>
                    <h1>{form.title || 'New session'}</h1>
                </div>
                <div className="row">
                    <button className="btn secondary" onClick={onCancel}>Cancel</button>
                    <button className="btn" disabled={!form.title} onClick={save}>Save session</button>
                </div>
            </div>

            {error && <div className="banner error" style={{ marginBottom: 16 }}>{error}</div>}

            <Card>
                <div className="stack">
                    <div className="field-row">
                        <TextField label="Title" value={form.title} onChange={(v) => update({ title: v })} />
                        <TextField label="Slug" value={form.slug} onChange={(v) => update({ slug: v })}
                            placeholder="derived from title" />
                    </div>
                    <TextField label="Summary" value={form.summary} onChange={(v) => update({ summary: v })}
                        placeholder="Focus: Push & Pull Mechanics" />
                    <div className="field-row">
                        <NumberField label="Estimated minutes"
                            value={Math.round((form.estimated_seconds ?? 0) / 60)}
                            onChange={(v) => update({ estimated_seconds: v * 60 })} />
                        <Field label="Free-tier access">
                            <label className="row-tight" style={{ fontWeight: 500 }}>
                                <input type="checkbox" checked={form.is_free_sample}
                                    onChange={(e) => update({ is_free_sample: e.target.checked })} />
                                Available on the Free tier
                            </label>
                        </Field>
                    </div>
                    <TagsField label="Tags" value={form.tags} onChange={(v) => update({ tags: v })} />
                    <MediaSlots moduleId={moduleId}
                        imageId={form.cover_media_id} videoId={form.video_media_id}
                        onImage={(id) => update({ cover_media_id: id })}
                        onVideo={(id) => update({ video_media_id: id })} />
                    <AttributeWeightsField
                        value={form.attribute_weights}
                        onChange={(weights) => update({ attribute_weights: weights })} />
                </div>
            </Card>

            <div style={{ marginTop: 20 }}>
                <div className="row" style={{ marginBottom: 10 }}>
                    <h2>Blocks</h2>
                    <div className="spacer" />
                    <button className="btn secondary small" onClick={() => update({
                        blocks: [...form.blocks, {
                            id: uid(), title: '', grouping: { straight: {} },
                            exercises: exercises[0] ? [emptyPrescription(exercises[0].id)] : [],
                        }],
                    })}>Add block</button>
                </div>

                {form.blocks.length === 0 && (
                    <Empty title="No blocks" hint="A block groups exercises under one execution rule — straight sets, a superset, a circuit." />
                )}

                {form.blocks.map((block, index) => (
                    <BlockEditor
                        key={block.id}
                        block={block}
                        exercises={exercises}
                        onChange={(b) => updateBlock(index, b)}
                        onRemove={() => update({ blocks: form.blocks.filter((_, i) => i !== index) })}
                        onMove={(direction) => {
                            const target = index + (direction === 'up' ? -1 : 1);
                            if (target < 0 || target >= form.blocks.length) return;
                            const next = form.blocks.slice();
                            [next[index], next[target]] = [next[target]!, next[index]!];
                            update({ blocks: next });
                        }}
                    />
                ))}
            </div>
        </>
    );
}

const GROUPINGS = [
    { value: 'straight', label: 'Straight sets' },
    { value: 'superset', label: 'Superset (alternating)' },
    { value: 'circuit', label: 'Circuit (rounds)' },
    { value: 'dropSet', label: 'Drop set' },
    { value: 'pyramid', label: 'Pyramid' },
    { value: 'flow', label: 'Flow (time-led)' },
] as const;

function groupingKind(grouping: SetGrouping): string {
    return Object.keys(grouping)[0]!;
}

function BlockEditor({ block, exercises, onChange, onRemove, onMove }: {
    block: ExerciseBlock; exercises: Exercise[];
    onChange: (block: ExerciseBlock) => void; onRemove: () => void;
    onMove: (direction: 'up' | 'down') => void;
}) {
    const kind = groupingKind(block.grouping);

    return (
        <div className="block">
            <div className="row">
                <input
                    style={{ maxWidth: 240 }}
                    value={block.title ?? ''}
                    placeholder="Block title (optional)"
                    onChange={(e) => onChange({ ...block, title: e.target.value })}
                />
                <select
                    style={{ maxWidth: 200 }}
                    value={kind}
                    onChange={(e) => {
                        const next = e.target.value;
                        const grouping: SetGrouping =
                            next === 'circuit' ? { circuit: { rounds: 3 } }
                            : next === 'pyramid' ? { pyramid: { ascending: true } }
                            : ({ [next]: {} } as SetGrouping);
                        onChange({ ...block, grouping });
                    }}
                >
                    {GROUPINGS.map((g) => <option key={g.value} value={g.value}>{g.label}</option>)}
                </select>

                {'circuit' in block.grouping && (
                    <input
                        type="number" className="input-narrow"
                        value={block.grouping.circuit.rounds}
                        onChange={(e) => onChange({ ...block, grouping: { circuit: { rounds: Number(e.target.value) } } })}
                        aria-label="Rounds"
                    />
                )}

                <div className="spacer" />
                <button className="btn icon secondary" onClick={() => onMove('up')} aria-label="Move block up">↑</button>
                <button className="btn icon secondary" onClick={() => onMove('down')} aria-label="Move block down">↓</button>
                <button className="btn danger small" onClick={onRemove}>Remove</button>
            </div>

            {block.exercises.map((prescription, index) => (
                <PrescriptionEditor
                    key={prescription.id}
                    prescription={prescription}
                    exercises={exercises}
                    onChange={(p) => onChange({
                        ...block,
                        exercises: block.exercises.map((x, i) => (i === index ? p : x)),
                    })}
                    onRemove={() => onChange({
                        ...block,
                        exercises: block.exercises.filter((_, i) => i !== index),
                    })}
                />
            ))}

            <button
                className="btn secondary small"
                style={{ marginTop: 10 }}
                disabled={exercises.length === 0}
                onClick={() => onChange({
                    ...block,
                    exercises: [...block.exercises, emptyPrescription(exercises[0]!.id)],
                })}
            >Add exercise</button>
        </div>
    );
}

function PrescriptionEditor({ prescription, exercises, onChange, onRemove }: {
    prescription: ExercisePrescription; exercises: Exercise[];
    onChange: (p: ExercisePrescription) => void; onRemove: () => void;
}) {
    const exercise = exercises.find((e) => e.id === prescription.exerciseID);
    const isRecorded = 'recorded' in prescription.completion;

    return (
        <div className="prescription">
            <div className="row">
                <select
                    style={{ maxWidth: 260 }}
                    value={prescription.exerciseID}
                    onChange={(e) => onChange({ ...prescription, exerciseID: e.target.value })}
                >
                    {exercises.map((e) => <option key={e.id} value={e.id}>{e.name}</option>)}
                </select>

                {/* The §8.4 decision, per prescription. Toggling this is what flips the
                    player between watch-and-continue and asking for numbers. */}
                <label className="row-tight small" style={{ fontWeight: 600 }}>
                    <input
                        type="checkbox"
                        checked={isRecorded}
                        onChange={(e) => onChange({
                            ...prescription,
                            completion: e.target.checked
                                ? { recorded: { _0: { dimensions: [], isRequired: false } } }
                                : { automatic: {} },
                        })}
                    />
                    Record performance
                </label>

                {isRecorded && exercise && (
                    <span className="small muted">
                        prompts for {exercise.measured_in.map((d) => DIMENSION_LABELS[d]).join(' + ')}
                    </span>
                )}

                <div className="spacer" />
                <button className="btn danger small" onClick={onRemove}>Remove</button>
            </div>

            <div style={{ marginTop: 8 }}>
                {prescription.sets.map((set, index) => (
                    <SetRow
                        key={index}
                        index={index}
                        set={set}
                        dimensions={exercise?.measured_in ?? ['repetitions']}
                        onChange={(s) => onChange({
                            ...prescription,
                            sets: prescription.sets.map((x, i) => (i === index ? s : x)),
                        })}
                        onRemove={() => onChange({
                            ...prescription,
                            sets: prescription.sets.filter((_, i) => i !== index),
                        })}
                    />
                ))}
                <button
                    className="btn secondary small"
                    style={{ marginTop: 8 }}
                    onClick={() => onChange({
                        ...prescription,
                        sets: [...prescription.sets, prescription.sets.at(-1)
                            ?? { targets: [{ dimension: 'repetitions', bound: { exactly: { _0: 10 } } }] }],
                    })}
                >Add set</button>
            </div>

            <div style={{ marginTop: 8 }}>
                <input
                    value={prescription.notes}
                    placeholder="Coaching note (optional)"
                    onChange={(e) => onChange({ ...prescription, notes: e.target.value })}
                />
            </div>
        </div>
    );
}

function boundKind(bound: Bound): string {
    return Object.keys(bound)[0]!;
}

function SetRow({ index, set, dimensions, onChange, onRemove }: {
    index: number; set: SetPrescription; dimensions: MeasurementDimension[];
    onChange: (set: SetPrescription) => void; onRemove: () => void;
}) {
    const target = set.targets[0] ?? { dimension: dimensions[0]!, bound: { exactly: { _0: 10 } } };
    const kind = boundKind(target.bound);

    function setBound(nextKind: string) {
        const bound: Bound =
            nextKind === 'between' ? { between: { _0: 8, _1: 12 } }
            : nextKind === 'atLeast' ? { atLeast: { _0: 10 } }
            : nextKind === 'toFailure' ? { toFailure: {} }
            : { exactly: { _0: 10 } };
        onChange({ ...set, targets: [{ ...target, bound }] });
    }

    return (
        <div className="set-row">
            <span className="small muted" style={{ width: 42 }}>Set {index + 1}</span>

            <select
                style={{ maxWidth: 120 }}
                value={target.dimension}
                onChange={(e) => onChange({
                    ...set,
                    targets: [{ ...target, dimension: e.target.value as MeasurementDimension }],
                })}
            >
                {dimensions.map((d) => <option key={d} value={d}>{DIMENSION_LABELS[d]}</option>)}
            </select>

            <select style={{ maxWidth: 120 }} value={kind} onChange={(e) => setBound(e.target.value)}>
                <option value="exactly">exactly</option>
                <option value="between">between</option>
                <option value="atLeast">at least</option>
                <option value="toFailure">to failure</option>
            </select>

            {'exactly' in target.bound && (
                <input type="number" className="input-narrow" value={target.bound.exactly._0}
                    onChange={(e) => onChange({ ...set, targets: [{ ...target, bound: { exactly: { _0: Number(e.target.value) } } }] })} />
            )}
            {'atLeast' in target.bound && (
                <input type="number" className="input-narrow" value={target.bound.atLeast._0}
                    onChange={(e) => onChange({ ...set, targets: [{ ...target, bound: { atLeast: { _0: Number(e.target.value) } } }] })} />
            )}
            {'between' in target.bound && (
                <>
                    <input type="number" className="input-narrow" value={target.bound.between._0}
                        onChange={(e) => onChange({ ...set, targets: [{ ...target, bound: { between: { _0: Number(e.target.value), _1: target.bound && 'between' in target.bound ? target.bound.between._1 : 0 } } }] })} />
                    <span className="small muted">to</span>
                    <input type="number" className="input-narrow" value={target.bound.between._1}
                        onChange={(e) => onChange({ ...set, targets: [{ ...target, bound: { between: { _0: target.bound && 'between' in target.bound ? target.bound.between._0 : 0, _1: Number(e.target.value) } } }] })} />
                </>
            )}

            <span className="small muted">rest</span>
            <input
                type="number" className="input-narrow"
                value={set.restAfter?.value ?? 0}
                onChange={(e) => onChange({
                    ...set,
                    restAfter: { dimension: 'duration', value: Number(e.target.value) },
                })}
                aria-label="Rest seconds"
            />
            <span className="small muted">s</span>

            <div className="spacer" />
            <button className="btn icon danger" onClick={onRemove} aria-label="Remove set">×</button>
        </div>
    );
}


/** The six radar axes, in the app's drawing order. */
const ATTRIBUTES = ['strength', 'flexibility', 'coordination', 'power', 'endurance', 'mobility'] as const;

/**
 * Evolution weights: how much of each attribute a completed run of this session earns.
 *
 * The scale is small integers (existing content uses 1–3; the engine normalises against
 * the athlete's strongest axis, but sessions from *different* modules merge into one
 * radar, so staying on one scale keeps them comparable). Zero means "does not train
 * this" and is stored as an absent key, so an untouched session keeps the clean `{}`
 * it always had. Without weights a session still awards XP but contributes nothing to
 * the radar chart — worth a sentence in the UI, because a flat radar looks like a bug
 * and is actually missing authoring.
 */
function AttributeWeightsField({ value, onChange }: {
    value: Record<string, number>;
    onChange: (weights: Record<string, number>) => void;
}) {
    function set(attribute: string, raw: number) {
        const clamped = Math.max(0, Math.min(5, Math.round(raw)));
        const next = { ...value };
        if (clamped === 0) delete next[attribute];
        else next[attribute] = clamped;
        onChange(next);
    }

    return (
        <Field label="Evolution weights — what this session trains (0–5 per axis; existing content uses 1–3)">
            <div className="stack" style={{ gap: 6 }}>
                <div className="row-tight" style={{ flexWrap: 'wrap', gap: 12 }}>
                    {ATTRIBUTES.map((attribute) => (
                        <label key={attribute} className="row-tight" style={{ gap: 6, fontWeight: 500 }}>
                            <span style={{ width: 92, textTransform: 'capitalize' }}>{attribute}</span>
                            <input
                                type="number"
                                className="input-narrow"
                                min={0} max={5} step={1}
                                value={value[attribute] ?? 0}
                                onChange={(e) => set(attribute, Number(e.target.value) || 0)}
                            />
                        </label>
                    ))}
                </div>
                <span className="small" style={{ color: 'var(--text-secondary, #6b6b6b)' }}>
                    Completing this session moves the athlete&rsquo;s radar by these amounts.
                    All zeros means it awards XP but never moves the radar.
                </span>
            </div>
        </Field>
    );
}
